/**
 * Walker incident manager for one walk: report a new incident (while IN_PROGRESS),
 * attach photos, and resolve open ones. Reads the live incident list so it stays
 * fresh after raise/resolve.
 */
import { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { form_error_message } from '@/lib/forms';
import { use_vendor_incidents, use_raise_incident, use_resolve_incident } from '@/features/walk-incident/hooks';
import {
  INCIDENT_TYPES,
  INCIDENT_TYPE_LABEL,
  type WalkIncident,
  type WalkIncidentSeverity,
  type WalkIncidentType,
} from '@/features/walk-incident/types';
import type { Booking } from '@/features/bookings/types';

const field =
  'flex w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15';

function Shell({ title, on_close, children }: { title: string; on_close: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={on_close}>
      <div className="my-12 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">{children}</div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, tone = 'primary' }: { active: boolean; onClick: () => void; children: React.ReactNode; tone?: 'primary' | 'danger' }) {
  const on = tone === 'danger' ? 'border-destructive bg-destructive/10 text-destructive' : 'border-primary bg-primary/10 text-primary';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-button border-2 px-3 py-1.5 text-xs font-semibold transition-all ${active ? on : 'border-input bg-card text-muted-foreground hover:border-primary/40'}`}
    >
      {children}
    </button>
  );
}

function ReportForm({ booking_id, on_done }: { booking_id: string; on_done: () => void }) {
  const raise_m = use_raise_incident(booking_id);
  const [type, set_type] = useState<WalkIncidentType | null>(null);
  const [severity, set_severity] = useState<WalkIncidentSeverity>('INFO');
  const [note, set_note] = useState('');
  const [error, set_error] = useState<string | null>(null);

  async function submit() {
    set_error(null);
    if (!type) { set_error('Pick an incident type.'); return; }
    if (!note.trim()) { set_error('Add a short note.'); return; }
    try {
      await raise_m.mutateAsync({ type, severity, note: note.trim() });
      set_type(null); set_severity('INFO'); set_note('');
      on_done();
    } catch (err) {
      set_error(form_error_message(err, 'Could not report the incident.'));
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-card border-2 border-dashed border-brand-outline-variant/30 p-3" data-testid="incident-report-form">
      <p className="text-sm font-semibold">Report an incident</p>
      <div className="flex flex-col gap-1.5">
        <Label>Type</Label>
        <div className="flex flex-wrap gap-1.5">
          {INCIDENT_TYPES.map((t) => (
            <Chip key={t} active={type === t} onClick={() => set_type(t)}>{INCIDENT_TYPE_LABEL[t]}</Chip>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Severity</Label>
        <div className="flex gap-1.5">
          <Chip active={severity === 'INFO'} onClick={() => set_severity('INFO')}>Info</Chip>
          <Chip active={severity === 'URGENT'} onClick={() => set_severity('URGENT')} tone="danger">Urgent</Chip>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="incident-note">What happened?</Label>
        <textarea id="incident-note" className={`${field} h-20 resize-y`} maxLength={2000} value={note} onChange={(e) => set_note(e.target.value)} placeholder="Describe the incident for the owner" data-testid="incident-note" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="incident-form-error">{error}</p>}
      <div>
        <Button variant="gradient" size="sm" onClick={submit} disabled={raise_m.isPending} data-testid="incident-report-submit">
          {raise_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Report &amp; notify owner
        </Button>
      </div>
    </div>
  );
}

function IncidentCard({ booking_id, incident }: { booking_id: string; incident: WalkIncident }) {
  const resolve_m = use_resolve_incident(booking_id);
  const [resolving, set_resolving] = useState(false);
  const [note, set_note] = useState('');
  const urgent = incident.severity === 'URGENT';
  const open = incident.status === 'OPEN';

  return (
    <div className="rounded-card border-2 border-border p-3" data-testid={`incident-card-${incident.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{INCIDENT_TYPE_LABEL[incident.type]}</span>
        {urgent && <span className="rounded-full bg-destructive/12 px-2 py-0.5 text-[11px] font-medium text-destructive">urgent</span>}
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${open ? 'bg-warning/15 text-warning' : 'bg-success/12 text-success'}`}>
          {open ? 'open' : 'resolved'}
        </span>
        {incident.owner_acknowledged_at && (
          <span className="ml-auto text-[11px] text-muted-foreground">owner acknowledged</span>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{incident.note}</p>
      {incident.resolution_note && <p className="mt-1 text-xs text-success">Resolved: {incident.resolution_note}</p>}

      <div className="mt-2">
        <ImageGalleryEditor owner_type="WALK_INCIDENT" owner_id={incident.id} label="Photos" />
      </div>

      {open && (
        resolving ? (
          <div className="mt-2 flex flex-col gap-2">
            <textarea className={`${field} h-16 resize-y`} maxLength={2000} value={note} onChange={(e) => set_note(e.target.value)} placeholder="Resolution note (optional)" data-testid={`incident-resolve-note-${incident.id}`} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => set_resolving(false)}>Cancel</Button>
              <Button variant="gradient" size="sm" disabled={resolve_m.isPending} data-testid={`incident-resolve-submit-${incident.id}`}
                onClick={async () => { await resolve_m.mutateAsync({ incident_id: incident.id, resolution_note: note.trim() || undefined }); set_resolving(false); }}>
                {resolve_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Mark resolved
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="mt-2" onClick={() => set_resolving(true)} data-testid={`incident-resolve-${incident.id}`}>
            Resolve
          </Button>
        )
      )}
    </div>
  );
}

export function WalkIncidentsModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const list_q = use_vendor_incidents(booking.id);
  const incidents = list_q.data ?? [];
  const can_report = booking.status === 'IN_PROGRESS';

  return (
    <Shell title="Walk incidents" on_close={on_close}>
      {can_report && <ReportForm booking_id={booking.id} on_done={() => list_q.refetch()} />}

      {list_q.isLoading ? (
        <div className="flex items-center justify-center p-6 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
      ) : incidents.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="incidents-empty">No incidents on this walk.</p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="incidents-list">
          {incidents.map((i) => <IncidentCard key={i.id} booking_id={booking.id} incident={i} />)}
        </div>
      )}
    </Shell>
  );
}
